import {
  type ForwardedRef,
  forwardRef,
  type InputHTMLAttributes,
  useCallback,
  useState,
} from "react";

import { TextField, type TextFieldProps } from "@shapr3d/frontend-lib";
import type React from "react";
import { z } from "zod";

import useTranslation from "../../hooks/useTranslation";

import style from "./ValidatedPasswordInput.module.css";

export const passwordSchema = z
  .string()
  .regex(/^.*[a-z].*$/, { message: "hasLowercase" })
  .regex(/^.*[A-Z].*$/, { message: "hasUppercase" })
  .regex(/^.*[0-9].*$/, { message: "hasNumber" })
  .min(12, { message: "minimumLength" })
  .max(72, { message: "maximumLength" });

export const ValidatedPasswordInput = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & TextFieldProps & { passwordConfirm?: string }
>(({ passwordConfirm, ...attrs }, ref: ForwardedRef<HTMLInputElement>) => {
  const [validationChecks, setValidationChecks] = useState<(JSX.Element | null)[]>([]);
  const [value, setValue] = useState("");
  const [tooLongPasswordError, setTooLongPasswordError] = useState(false);
  const [validationSuccess, setValidationSuccess] = useState(true);
  const [hasInteracted, setHasInteracted] = useState(false);

  const { t, m } = useTranslation();

  type CheckTypes = "hasLowercase" | "hasUppercase" | "hasNumber" | "minimumLength";

  const checkPassword = useCallback(
    (value: string) => {
      const result = passwordSchema.safeParse(value);

      setTooLongPasswordError(false);

      const checks = {
        hasLowercase: {
          success: true,
          message: t(m.PasswordValidation.HasLowercase),
        },
        hasUppercase: {
          success: true,
          message: t(m.PasswordValidation.HasUppercase),
        },
        hasNumber: {
          success: true,
          message: t(m.PasswordValidation.HasNumber),
        },
        minimumLength: {
          success: true,
          message: t(m.PasswordValidation.MinimumLength),
        },
      } as { [K in CheckTypes]: { success: boolean; message: string } };

      if (!result.success) {
        result.error.issues
          .filter((issue) => issue.message !== "maximumLength")
          .forEach((issue) => {
            checks[issue.message as CheckTypes].success = false;
          });

        if (result.error.issues.some((issue) => issue.message === "maximumLength")) {
          setTooLongPasswordError(true);
        }
      }

      setValidationChecks(
        Object.values(checks).map((check, i) => {
          return (
            <li key={i} className={check.success ? style.passed : style.failed}>
              <span className={style.checkIcon}>{check.success ? "✓" : "x"}</span>
              {check.message}
            </li>
          );
        })
      );
      setValidationSuccess(
        Object.values(checks).reduce((prev, curr) => prev && curr.success, true)
      );
    },
    [m, t]
  );

  const showRequirements =
    hasInteracted && (!validationSuccess || !passwordConfirm || value !== passwordConfirm);

  return (
    <div className={style.wrapper}>
      <TextField
        type="password"
        {...attrs}
        errorMessage={tooLongPasswordError ? t(m.ErrorMessage.TooLongPassword) : attrs.errorMessage}
        onFocus={(event: React.FocusEvent<HTMLInputElement>) => {
          attrs.onFocus?.(event);
        }}
        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
          attrs.onBlur?.(event);
        }}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
          setValue(event.target.value);
          checkPassword(event.target.value);
          setHasInteracted(true);
          attrs.onChange?.(event);
        }}
        ref={ref}
      />
      {showRequirements && <ul className={style.checkList}>{validationChecks}</ul>}
    </div>
  );
});

ValidatedPasswordInput.displayName = "ValidatedPasswordInputRedesign";
